import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, CardBody, CardHeader, Button, ButtonGroup, Table, Nav, NavItem, NavLink, TabContent, TabPane, Form, FormGroup, Label, Input, Badge, Progress } from 'reactstrap';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler } from 'chart.js';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getAnalyticsData, getCustomReport, getReports, exportReport } from '../../../store/Admin';
import ViewStateManager from '../../../utils/viewStateManager';
import '../Admin.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, RadialLinearScale, Title, Tooltip, Legend, Filler);

const AnalyticsReporting = () => {
  const dispatch = useDispatch();
  const { analyticsData = {}, customReports, loading, reportGenerating } = useSelector(state => state['Admin']);
  const reportsArray = Array.isArray(customReports) ? customReports : [];
  const revenueDetails = analyticsData?.revenueDetails || {};
  const performanceDetails = analyticsData?.performanceDetails || {};

  const [activeTab, setActiveTab] = useState(() => ViewStateManager.getAdminSubTab('analytics', 'revenue')['activeTab']);

  // Persist sub-tab selection so a refresh restores the same tab
  useEffect(() => {
    ViewStateManager.setAdminSubTab('analytics', { activeTab });
  }, [activeTab]);

  const [dateRange, setDateRange] = useState({ 'start': new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'end': new Date() });
  const [selectedMetrics, setSelectedMetrics] = useState(['revenue', 'users', 'agents']);
  const [reportType, setReportType] = useState('summary');
  const [exportFormat, setExportFormat] = useState('pdf');

  useEffect(() => {
    dispatch(getAnalyticsData({ 'dateRange': dateRange, 'metrics': selectedMetrics }));
    dispatch(getReports()); // Fetch existing reports
  }, [dateRange, selectedMetrics]);

  const handleDateChange = (type, date) => {
    setDateRange(prev => ({ ...prev, [type]: date }));
  };

  const handleExport = (report) => {
    // If report object passed, use its data
    if (report && typeof report === 'object') {
      dispatch(exportReport({ 
        'format': 'txt', 
        'data': report.data || report, 
        'dateRange': dateRange,
        'reportId': report.id
      }));
    } else {
      // Generate new report based on current selection
      dispatch(exportReport({ 
        'format': 'txt', 
        'data': analyticsData, 
        'dateRange': dateRange,
        'reportId': report
      }));
    }
  };

  const generateCustomReport = () => {
    dispatch(getCustomReport({ 
      'type': reportType, 
      'dateRange': dateRange, 
      'metrics': selectedMetrics,
      'format': 'txt'
    }));
  };

  // Revenue trend chart
  const revenueTrendData = { 
    'labels': revenueDetails?.trend?.labels || analyticsData['revenueTrend']?.['labels'] || ['Week 1', 'Week 2', 'Week 3', 'Week 4'], 
    'datasets': [{
      'label': 'Revenue', 
      'data': revenueDetails?.trend?.revenue || analyticsData['revenueTrend']?.['data'] || [25000, 30000, 28000, 35000], 
      'borderColor': '#2E2C9ACC', 
      'backgroundColor': '#2E2C9ACC', 
      'fill': true, 
      'tension': 0.4 
    }, { 
      'label': 'Projected', 
      'data': revenueDetails?.trend?.projected || analyticsData['revenueTrend']?.['projected'] || [35000, 38000, 42000, 45000], 
      'borderColor': '#D7D6F6', 
      'backgroundColor': '#D7D6F6', 
      'borderDash': [5, 5], 
      'fill': true, 
      'tension': 0.4 
    }] 
  };

  // User engagement chart
  const userEngagementData = { 'labels': analyticsData['engagement']?.['labels'] || ['Login', 'Agent Creation', 'Calls Made', 'Deals Closed', 'Reports Viewed'], 'datasets': [{ 'label': 'This Period', 'data': analyticsData['engagement']?.['current'] || [85, 72, 68, 45, 60], 'backgroundColor': '#94C8FB80', 'borderColor': '#94C8FB', 'borderWidth': 2 }, { 'label': 'Previous Period', 'data': analyticsData['engagement']?.['previous'] || [78, 65, 60, 40, 55], 'backgroundColor': 'var(--primary-color)70', 'borderColor': 'var(--primary-color)', 'borderWidth': 2 }] };

  // Conversion funnel
  const conversionFunnelData = { 
    'labels': performanceDetails?.funnel?.stages || analyticsData['funnel']?.['stages'] || ['Leads', 'Qualified', 'Contacted', 'Negotiation', 'Closed'], 
    'datasets': [{ 
      'label': 'Conversion Rate', 
      'data': performanceDetails?.funnel?.values || analyticsData['funnel']?.['rates'] || [100, 75, 50, 30, 15], 
      'backgroundColor': ['#7F8BFA', 'var(--primary-color)', '#67C8FF', '#F7931A', '#94C8FB'], 
      'borderWidth': 0 
    }] 
  };

  // Agent performance distribution
  const agentPerformanceData = { 
    'labels': performanceDetails?.distribution?.labels || analyticsData['agentPerformance']?.['labels'] || ['Excellent', 'Good', 'Average', 'Below Average', 'Poor'], 
    'datasets': [{ 
      'data': performanceDetails?.distribution?.values || analyticsData['agentPerformance']?.['data'] || [15, 35, 30, 15, 5], 
      'backgroundColor': ['var(--primary-color)', '#7F8BFA', '#F7931A', '#67C8FF', '#94C8FB'] 
    }] 
  };

  // Only show loading when actually fetching analytics data
  if (loading) {
    return <Container fluid className="mt-4"><div className="text-center">Loading analytics...</div></Container>;
  }

  return (

    <>
      <div className='content-dashboard'>
        <div className='main-title'>
          <div className='left-area'>
            <h3>Analytics & Reporting</h3>
            <p className="text-muted mb-3">Comprehensive platform analytics and custom reports</p>
          </div>
          <div className='right-area'>
            {/* <div className='btn-group-selection'>
              <button className={`select-btn-format ${exportFormat === 'pdf' ? 'active' : ''}`} onClick={() => setExportFormat('pdf')} active={exportFormat === 'pdf'}>PDF</button>
              <button className={`select-btn-format ${exportFormat === 'pdf' ? 'excel' : ''}`} onClick={() => setExportFormat('excel')} active={exportFormat === 'excel'}>Excel</button>
              <button className={`select-btn-format ${exportFormat === 'pdf' ? 'csv' : ''}`} onClick={() => setExportFormat('csv')} active={exportFormat === 'csv'}>CSV</button>
            </div> */}
            {/* <button className="btn-style-new mt-2" onClick={handleExport}>
              <i className="icon fa fa-download"></i>Export Report
            </button> */}
          </div>
        </div>
        <div className='filter-box-header'>
          <Form className="dashboard-form security-form">
            <div className='row'>
              <div className='col-lg-3 col-md-6 col-sm-12'>
                <FormGroup className="mb-0">
                  <Label>Start Date</Label>
                  <DatePicker selected={dateRange['start']} onChange={(date) => handleDateChange('start', date)} className="form-control" />
                </FormGroup>
              </div>
              <div className='col-lg-3 col-md-6 col-sm-12'>
                <FormGroup className="mb-0">
                  <Label>End Date</Label>
                  <DatePicker selected={dateRange['end']} onChange={(date) => handleDateChange('end', date)} className="form-control" />
                </FormGroup>
              </div>
              <div className='col-lg-6 col-sm-12'>
                <div className='btn-group-selection'>
                  <button className={`select-btn-format ${dateRange.start?.toDateString() === new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toDateString() && dateRange.end?.toDateString() === new Date().toDateString() ? 'active' : ''}`} onClick={() => setDateRange({ 'start': new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), 'end': new Date() })}>Last 7 Days</button>
                  <button className={`select-btn-format ${dateRange.start?.toDateString() === new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toDateString() && dateRange.end?.toDateString() === new Date().toDateString() ? 'active' : ''}`} onClick={() => setDateRange({ 'start': new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'end': new Date() })}>Last 30 Days</button>
                  <button className={`select-btn-format ${dateRange.start?.toDateString() === new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toDateString() && dateRange.end?.toDateString() === new Date().toDateString() ? 'active' : ''}`} onClick={() => setDateRange({ 'start': new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), 'end': new Date() })}>Last 90 Days</button>
                </div>
              </div>
            </div>
          </Form>
        </div>

        <div className='tabs-area mt-3'>
          <div className='tab-header'>
            {/* Navigation Tabs */}
            <Nav tabs className="">
              <NavItem>
                <NavLink className={activeTab === 'revenue' ? 'active' : ''} onClick={() => setActiveTab('revenue')}>Overview & Analytics</NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'engagement' ? 'active' : ''} onClick={() => setActiveTab('engagement')}>User Engagement</NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'performance' ? 'active' : ''} onClick={() => setActiveTab('performance')}>Performance</NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'custom' ? 'active' : ''} onClick={() => setActiveTab('custom')}>Custom Reports</NavLink>
              </NavItem>
            </Nav>
          </div>
        </div>

        <TabContent activeTab={activeTab}>
          {/* Revenue Analytics Tab */}
          <TabPane tabId="revenue">
            {/* Overview Counters */}
            <div className='counter-sections mt-3'>
              <div className='counter-box primery-box'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Total Revenue</h5>
                    <div className='CountUp-box'>
                      <h3>${(revenueDetails?.total || analyticsData['overview']?.['totalRevenue'] || 0).toLocaleString()}</h3>
                    </div>
                    <small className={(revenueDetails?.growth || analyticsData['overview']?.['revenueGrowth'] || 0) >= 0 ? 'text-success' : 'text-danger'}>
                      <i className={`icon-graph`}>
                        {(revenueDetails?.growth || analyticsData['overview']?.['revenueGrowth'] || 0) >= 0
                          ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                          : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                        }
                      </i>
                      {Math.abs(revenueDetails?.growth || analyticsData['overview']?.['revenueGrowth'] || 0)}%
                    </small>
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle opacity="0.1" cx="16" cy="16" r="16" fill="#9333EA" />
                        <path d="M16.9334 19.6133H15.2601C14.1668 19.6133 13.2801 18.6933 13.2801 17.56C13.2801 17.2867 13.5068 17.06 13.7801 17.06C14.0534 17.06 14.2801 17.2867 14.2801 17.56C14.2801 18.14 14.7201 18.6133 15.2601 18.6133H16.9334C17.3668 18.6133 17.7268 18.2267 17.7268 17.76C17.7268 17.18 17.5201 17.0667 17.1801 16.9467L14.4934 16C13.9734 15.82 13.2734 15.4333 13.2734 14.24C13.2734 13.2133 14.0801 12.3867 15.0668 12.3867H16.7401C17.8334 12.3867 18.7201 13.3067 18.7201 14.44C18.7201 14.7133 18.4934 14.94 18.2201 14.94C17.9468 14.94 17.7201 14.7133 17.7201 14.44C17.7201 13.86 17.2801 13.3867 16.7401 13.3867H15.0668C14.6334 13.3867 14.2734 13.7733 14.2734 14.24C14.2734 14.82 14.4801 14.9333 14.8201 15.0533L17.5068 16C18.0268 16.18 18.7268 16.5667 18.7268 17.76C18.7201 18.78 17.9201 19.6133 16.9334 19.6133Z" fill="#9333EA" />
                        <path d="M16 20.5C15.7267 20.5 15.5 20.2733 15.5 20V12C15.5 11.7267 15.7267 11.5 16 11.5C16.2733 11.5 16.5 11.7267 16.5 12V20C16.5 20.2733 16.2733 20.5 16 20.5Z" fill="#9333EA" />
                        <path d="M16.0002 23.1666C12.0468 23.1666 8.8335 19.9533 8.8335 16C8.8335 12.0466 12.0468 8.83331 16.0002 8.83331C19.9535 8.83331 23.1668 12.0466 23.1668 16C23.1668 19.9533 19.9535 23.1666 16.0002 23.1666ZM16.0002 9.83331C12.6002 9.83331 9.8335 12.6 9.8335 16C9.8335 19.4 12.6002 22.1666 16.0002 22.1666C19.4002 22.1666 22.1668 19.4 22.1668 16C22.1668 12.6 19.4002 9.83331 16.0002 9.83331Z" fill="#9333EA" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className='counter-box success-box'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Active Users</h5>
                    <div className='CountUp-box'>
                      <h3 className="text-success">{analyticsData['overview']?.['activeUsers']?.toLocaleString() || '0'}</h3>
                    </div>
                    <small className={analyticsData['overview']?.['userGrowth'] >= 0 ? 'text-success' : 'text-danger'}>
                      <i className={`icon-graph`}>
                        {analyticsData['overview']?.['userGrowth'] >= 0
                          ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                          : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                        }
                      </i>
                      {Math.abs(analyticsData['overview']?.['userGrowth'] || 0)}%
                    </small>
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle opacity="0.1" cx="16" cy="16" r="16" fill="#6DA92F" />
                        <path d="M14.1062 15.7467C14.0862 15.7467 14.0729 15.7467 14.0529 15.7467C14.0196 15.74 13.9729 15.74 13.9329 15.7467C11.9996 15.6867 10.5396 14.1667 10.5396 12.2934C10.5396 10.3867 12.0929 8.83337 13.9996 8.83337C15.9062 8.83337 17.4596 10.3867 17.4596 12.2934C17.4529 14.1667 15.9862 15.6867 14.1262 15.7467C14.1196 15.7467 14.1129 15.7467 14.1062 15.7467ZM13.9996 9.83337C12.6462 9.83337 11.5396 10.94 11.5396 12.2934C11.5396 13.6267 12.5796 14.7 13.9062 14.7467C13.9462 14.74 14.0329 14.74 14.1196 14.7467C15.4262 14.6867 16.4529 13.6134 16.4596 12.2934C16.4596 10.94 15.3529 9.83337 13.9996 9.83337Z" fill="#6DA92F" />
                        <path d="M19.0261 15.8333C19.0061 15.8333 18.9861 15.8333 18.9661 15.8266C18.6928 15.8533 18.4128 15.66 18.3861 15.3866C18.3594 15.1133 18.5261 14.8666 18.7994 14.8333C18.8794 14.8266 18.9661 14.8266 19.0394 14.8266C20.0128 14.7733 20.7728 13.9733 20.7728 12.9933C20.7728 11.98 19.9528 11.16 18.9394 11.16C18.6661 11.1666 18.4394 10.94 18.4394 10.6666C18.4394 10.3933 18.6661 10.1666 18.9394 10.1666C20.4994 10.1666 21.7728 11.44 21.7728 13C21.7728 14.5333 20.5728 15.7733 19.0461 15.8333C19.0394 15.8333 19.0328 15.8333 19.0261 15.8333Z" fill="#6DA92F" />
                        <path d="M14.1131 23.0333C12.8064 23.0333 11.4931 22.7 10.4997 22.0333C9.57307 21.42 9.06641 20.58 9.06641 19.6666C9.06641 18.7533 9.57307 17.9066 10.4997 17.2866C12.4997 15.96 15.7397 15.96 17.7264 17.2866C18.6464 17.9 19.1597 18.74 19.1597 19.6533C19.1597 20.5666 18.6531 21.4133 17.7264 22.0333C16.7264 22.7 15.4197 23.0333 14.1131 23.0333ZM11.0531 18.1266C10.4131 18.5533 10.0664 19.1 10.0664 19.6733C10.0664 20.24 10.4197 20.7866 11.0531 21.2066C12.7131 22.32 15.5131 22.32 17.1731 21.2066C17.8131 20.78 18.1597 20.2333 18.1597 19.66C18.1597 19.0933 17.8064 18.5466 17.1731 18.1266C15.5131 17.02 12.7131 17.02 11.0531 18.1266Z" fill="#6DA92F" />
                        <path d="M20.2263 21.8333C19.993 21.8333 19.7863 21.6733 19.7396 21.4333C19.6863 21.16 19.8596 20.9 20.1263 20.84C20.5463 20.7533 20.933 20.5867 21.233 20.3533C21.613 20.0667 21.8196 19.7067 21.8196 19.3267C21.8196 18.9467 21.613 18.5867 21.2396 18.3067C20.9463 18.08 20.5796 17.92 20.1463 17.82C19.8796 17.76 19.7063 17.4933 19.7663 17.22C19.8263 16.9533 20.093 16.78 20.3663 16.84C20.9396 16.9667 21.4396 17.1933 21.8463 17.5067C22.4663 17.9733 22.8196 18.6333 22.8196 19.3267C22.8196 20.02 22.4596 20.68 21.8396 21.1533C21.4263 21.4733 20.9063 21.7067 20.333 21.82C20.293 21.8333 20.2596 21.8333 20.2263 21.8333Z" fill="#6DA92F" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className='counter-box warning-box'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Conversion Rate</h5>
                    <div className='CountUp-box'>
                      <h3>{performanceDetails?.conversionRate || analyticsData['overview']?.['conversionRate'] || '0'}%</h3>
                    </div>
                    <Progress value={performanceDetails?.conversionRate || analyticsData['overview']?.['conversionRate'] || 0} color="warning" />
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle opacity="0.1" cx="16" cy="16" r="16" fill="#DF9524" />
                        <path d="M12.8402 20.5933C12.7868 20.5933 12.7335 20.5866 12.6802 20.58C12.2802 20.5266 11.9402 20.2733 11.7735 19.9133L10.9335 18.0666C9.60016 17.0799 8.8335 15.6066 8.8335 14.0066C8.8335 11.16 11.3002 8.83997 14.3335 8.83997C16.4735 8.83997 18.4268 10.0133 19.3135 11.8333C19.6535 12.5066 19.8335 13.24 19.8335 14.0066C19.8335 16.78 17.4935 19.0533 14.5735 19.1666L13.7335 20.18C13.5068 20.44 13.1802 20.5933 12.8402 20.5933ZM14.3335 9.83329C11.8535 9.83329 9.8335 11.7 9.8335 14C9.8335 15.3133 10.4868 16.5266 11.6202 17.3266C11.6935 17.38 11.7535 17.4466 11.7868 17.5266L12.6802 19.4933C12.7135 19.5666 12.7802 19.5866 12.8135 19.5866C12.8468 19.5933 12.9135 19.5866 12.9602 19.5333L13.9468 18.3466C14.0402 18.2333 14.1802 18.1666 14.3335 18.1666C16.8135 18.1666 18.8335 16.3 18.8335 14C18.8335 13.3933 18.6935 12.8133 18.4202 12.2733C17.7002 10.7866 16.0935 9.83329 14.3335 9.83329Z" fill="#DF9524" />
                        <path d="M19.1602 23.1667C18.8136 23.1667 18.4935 23.0134 18.2669 22.7467L17.4269 21.74C15.8135 21.6734 14.2935 20.9333 13.3202 19.7333C13.1669 19.5467 13.1736 19.28 13.3269 19.1L13.9469 18.3534C14.0402 18.2401 14.1802 18.1734 14.3336 18.1734C16.8136 18.1734 18.8336 16.3067 18.8336 14.0067C18.8336 13.4 18.6936 12.82 18.4202 12.28C18.3336 12.1067 18.3536 11.9 18.4736 11.7467C18.5936 11.5934 18.7936 11.5267 18.9802 11.5667C21.4469 12.1333 23.1669 14.1934 23.1669 16.58C23.1669 18.18 22.4002 19.6534 21.0669 20.64L20.2269 22.4867C20.0535 22.86 19.7136 23.1134 19.3069 23.16C19.2602 23.1667 19.2069 23.1667 19.1602 23.1667ZM14.3735 19.4C15.2069 20.2467 16.4069 20.74 17.6669 20.74C17.8136 20.74 17.9536 20.8067 18.0536 20.92L19.0402 22.1067C19.0869 22.1667 19.1469 22.1734 19.1869 22.1667C19.2135 22.1667 19.2869 22.1467 19.3269 22.0667L20.2202 20.1C20.2602 20.02 20.3136 19.9467 20.3869 19.9C21.5202 19.1 22.1735 17.8867 22.1735 16.5734C22.1735 14.9734 21.1936 13.5534 19.7002 12.8534C19.7936 13.2267 19.8402 13.6067 19.8402 14C19.8402 16.7734 17.5002 19.0467 14.5802 19.16L14.3735 19.4Z" fill="#DF9524" />
                        <path d="M15.9998 14.5H12.6665C12.3932 14.5 12.1665 14.2733 12.1665 14C12.1665 13.7267 12.3932 13.5 12.6665 13.5H15.9998C16.2732 13.5 16.4998 13.7267 16.4998 14C16.4998 14.2733 16.2732 14.5 15.9998 14.5Z" fill="#FF7A59" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className='counter-box danger-box'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Avg Deal Size</h5>
                    <div className='CountUp-box'>
                      <h3>${(revenueDetails?.avgDealSize || analyticsData['overview']?.['avgDealSize'] || 0).toLocaleString()}</h3>
                    </div>
                    <small className="text-muted">Per closed deal</small>
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle opacity="0.1" cx="16" cy="16" r="16" fill="#C73A63" />
                        <path d="M15.8433 23.9988C15.4213 23.9311 15.1004 23.6955 14.8111 23.3973C14.5826 23.1616 14.336 22.9436 14.1047 22.7107C13.9513 22.5565 13.7775 22.5076 13.5642 22.5421C13.1681 22.6059 12.7704 22.6634 12.3724 22.7127C11.4921 22.8219 10.8553 22.3346 10.7101 21.4508C10.6479 21.0712 10.5778 20.6923 10.5148 20.3127C10.4808 20.1068 10.3739 19.9643 10.186 19.8688C9.824 19.6849 9.46549 19.4935 9.10814 19.3001C8.36489 18.8986 8.11596 18.1353 8.47956 17.3733C8.65413 17.007 8.83182 16.6422 9.01577 16.2805C9.11362 16.088 9.11284 15.9071 9.01499 15.715C8.83338 15.358 8.65843 14.9975 8.48504 14.6367C8.11244 13.8617 8.3641 13.0958 9.12419 12.6887C9.47331 12.5016 9.82048 12.3114 10.1747 12.1349C10.3813 12.0316 10.4855 11.8754 10.5211 11.6519C10.5845 11.2511 10.6514 10.8507 10.732 10.4535C10.9043 9.60922 11.5712 9.15677 12.4233 9.29062C12.8197 9.35285 13.2174 9.40608 13.6147 9.46323C13.7978 9.48984 13.9516 9.43544 14.0847 9.3055C14.3826 9.01391 14.6828 8.72428 14.9845 8.4366C15.5951 7.8546 16.4041 7.8546 17.0151 8.43582C17.3094 8.71606 17.6045 8.99629 17.8918 9.28397C18.0503 9.44248 18.228 9.49102 18.4468 9.4554C18.8429 9.39082 19.2402 9.33094 19.6386 9.28514C20.5063 9.18573 21.1271 9.65227 21.2836 10.5133C21.3525 10.8922 21.4206 11.2711 21.4793 11.6511C21.5138 11.875 21.6183 12.0316 21.8249 12.1349C22.1741 12.3094 22.5165 12.4985 22.8613 12.6821C23.6465 13.1001 23.8938 13.8641 23.5036 14.6645C23.3373 15.0058 23.1764 15.3502 23.0003 15.6864C22.8887 15.8993 22.8864 16.0946 22.9979 16.3083C23.1807 16.6586 23.3506 17.016 23.5205 17.3729C23.8844 18.1365 23.6371 18.8978 22.8938 19.3005C22.5412 19.4915 22.1885 19.6829 21.8296 19.8618C21.6167 19.9679 21.5134 20.1287 21.4782 20.3585C21.4171 20.7542 21.3494 21.1495 21.2695 21.5417C21.0973 22.3882 20.4343 22.8411 19.5819 22.7092C19.1854 22.6477 18.7873 22.5945 18.3901 22.537C18.2073 22.5104 18.0519 22.5601 17.9188 22.6908C17.6769 22.928 17.4253 23.1554 17.1893 23.3984C16.8996 23.6963 16.5787 23.9323 16.1572 24C16.0523 23.9988 15.9478 23.9988 15.8433 23.9988ZM19.8347 21.8098C20.1102 21.8184 20.3032 21.6728 20.3513 21.4227C20.4198 21.0653 20.4965 20.708 20.5388 20.3471C20.614 19.7013 20.9279 19.2461 21.5251 18.9745C21.8422 18.8305 22.1443 18.6532 22.4523 18.4888C22.7627 18.3228 22.841 18.0872 22.6887 17.7733C22.5118 17.4081 22.3361 17.0426 22.1525 16.6805C21.922 16.225 21.9216 15.7741 22.1517 15.3185C22.3298 14.9658 22.5005 14.6089 22.6735 14.2535C22.8453 13.9005 22.7733 13.6797 22.4253 13.4954C22.0664 13.3052 21.7067 13.1161 21.3482 12.9251C20.9373 12.7059 20.6758 12.374 20.5917 11.9122C20.5122 11.4773 20.4382 11.0413 20.3564 10.6073C20.2989 10.302 20.0868 10.1509 19.7822 10.1932C19.3846 10.2484 18.9869 10.3028 18.5908 10.3662C18.0636 10.4507 17.6135 10.3145 17.2347 9.93407C16.9513 9.64992 16.6593 9.37438 16.3693 9.09688C16.1235 8.86165 15.8738 8.86283 15.626 9.1008C15.325 9.38965 15.0264 9.68045 14.7258 9.96969C14.3951 10.2879 14.0041 10.4382 13.5434 10.3779C13.1094 10.3212 12.6761 10.2593 12.2428 10.1963C11.9176 10.1489 11.7027 10.2926 11.642 10.6128C11.5771 10.9552 11.5007 11.2965 11.4624 11.6421C11.39 12.2957 11.0706 12.7529 10.4682 13.0265C10.1606 13.1662 9.86784 13.3388 9.56881 13.4977C9.22908 13.6782 9.15511 13.9016 9.32184 14.2441C9.49719 14.6042 9.67058 14.9651 9.85023 15.3232C10.0765 15.7737 10.0772 16.2206 9.8514 16.6715C9.67214 17.0297 9.49797 17.3905 9.32263 17.7506C9.15511 18.0943 9.2283 18.3185 9.56607 18.4982C9.92029 18.6864 10.2733 18.8763 10.6295 19.0606C11.0898 19.299 11.3485 19.6747 11.4287 20.1839C11.491 20.5792 11.5634 20.9733 11.6381 21.3667C11.7035 21.7115 11.9117 21.8505 12.2553 21.7988C12.6522 21.7389 13.0499 21.6857 13.4467 21.627C13.9638 21.5507 14.401 21.7029 14.7704 22.0696C15.044 22.3413 15.3247 22.6062 15.6022 22.874C15.8851 23.1468 16.1157 23.1452 16.4018 22.8693C16.6718 22.6086 16.9482 22.3542 17.2104 22.0861C17.6077 21.6798 18.0777 21.5405 18.6355 21.6387C19.0409 21.71 19.4507 21.7557 19.8347 21.8098Z" fill="#C73A63" />
                        <path d="M19.5297 12.97C19.4906 13.0585 19.4573 13.1497 19.4107 13.2338C19.3837 13.2824 19.336 13.3199 19.2953 13.3602C17.3152 15.3372 15.3351 17.3145 13.3516 19.2879C13.2631 19.376 13.1469 19.4546 13.029 19.4903C12.8439 19.5462 12.6482 19.4409 12.5512 19.2746C12.4533 19.1063 12.4694 18.9043 12.5954 18.7501C12.6283 18.7098 12.6658 18.6734 12.7026 18.6366C14.6831 16.6597 16.6639 14.6836 18.6436 12.7062C18.7567 12.5935 18.8722 12.4945 19.0452 12.4949C19.3066 12.4957 19.5117 12.6996 19.5297 12.97Z" fill="#C73A63" />
                        <path d="M14.1551 15.2265C13.2216 15.2293 12.4639 14.477 12.46 13.5447C12.4561 12.6167 13.2126 11.8519 14.141 11.8453C15.0725 11.8386 15.8455 12.6061 15.8455 13.538C15.8455 14.468 15.0886 15.2242 14.1551 15.2265ZM14.1524 12.774C13.7285 12.774 13.3864 13.1114 13.3821 13.5337C13.3778 13.9533 13.7387 14.3126 14.159 14.3075C14.5774 14.302 14.923 13.9513 14.9199 13.5349C14.9171 13.1118 14.5766 12.774 14.1524 12.774Z" fill="#C73A63" />
                        <path d="M19.5391 18.4653C19.5375 19.3921 18.7735 20.153 17.8459 20.1518C16.9187 20.1507 16.1543 19.3871 16.1543 18.4622C16.1543 17.5236 16.9167 16.7663 17.8569 16.7718C18.7931 16.7776 19.5406 17.5299 19.5391 18.4653ZM17.8486 19.2235C18.2733 19.2227 18.6146 18.8845 18.6169 18.4622C18.6193 18.0465 18.2729 17.6966 17.8537 17.6911C17.4314 17.6857 17.0745 18.0426 17.0792 18.4649C17.0839 18.8888 17.424 19.2242 17.8486 19.2235Z" fill="#C73A63" />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-8 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Revenue Trend & Projection</h4>
                      <div style={{ height: '300px' }}>
                        <Line data={revenueTrendData} options={{ 'responsive': true, 'maintainAspectRatio': false, 'plugins': { 'legend': { 'position': 'top' } } }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-lg-4 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Top Performing Agents</h4>
                      <Table size="sm">
                        <thead>
                          <tr>
                            <th>Agent</th>
                            <th>Calls</th>
                            <th>Conversion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(performanceDetails?.topAgents || analyticsData['topAgents'] || []).map((agent, idx) => (
                            <tr key={idx}>
                              <td>{agent['name'] || agent.agentName || 'Agent ' + (idx + 1)}</td>
                              <td>{agent['calls'] || agent.totalCalls || 0}</td>
                              <td><Badge color="success">{agent['conversion'] || agent.conversionRate || 0}%</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>

            </div>
            
            {/* Overview Counters */}
            <div className='counter-sections mt-3'>
              <div className='counter-box primery-box'>
                <div className='counter-start' >
                  <div className='left-text-area'>
                    <h5>Total Revenue</h5>
                    <div className='CountUp-box'>
                      <h3>${(revenueDetails?.total || analyticsData['overview']?.['totalRevenue'] || 0).toLocaleString()}</h3>
                    </div>
                    <small className={(revenueDetails?.growth || analyticsData['overview']?.['revenueGrowth'] || 0) >= 0 ? 'text-success' : 'text-danger'}>
                      <i className={`icon-graph`}>
                        {(revenueDetails?.growth || analyticsData['overview']?.['revenueGrowth'] || 0) >= 0
                          ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                          : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                        }
                      </i>
                      {Math.abs(revenueDetails?.growth || analyticsData['overview']?.['revenueGrowth'] || 0)}%
                    </small>
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24.3741 11.416V8.82398C24.3741 4.67198 24.3741 4.67198 20.3901 2.75198L17.0301 1.02398C16.1501 0.567978 14.7181 0.567978 13.8381 1.02398L10.4781 2.75198C6.49407 4.67198 6.49407 4.67198 6.49407 8.82398V11.416C6.49407 15.568 6.49407 15.568 10.4781 17.488L13.8381 19.216C14.2781 19.444 14.8061 19.558 15.3341 19.558C15.8621 19.558 16.3901 19.444 16.8301 19.216L20.1901 17.488C24.1741 15.568 24.1741 15.568 24.1741 11.416Z" fill="white" /><path d="M33 20.416V24.568C33 28.72 33 28.72 29.016 30.64L25.656 32.368C25.216 32.596 24.688 32.71 24.16 32.71C23.632 32.71 23.104 32.596 22.664 32.368L19.504 30.734C19.32 30.636 19.16 30.518 19.008 30.394C18.424 29.932 18.424 29.236 18.73 28.61C19.036 27.984 19.642 27.648 20.118 27.876L23.29 29.51C23.788 29.752 24.532 29.752 25.03 29.51L28.39 27.782C29.552 27.172 29.552 26.59 29.552 25.178V20.416C29.552 19.004 29.552 18.422 28.39 17.812L25.03 16.084C24.782 15.958 24.444 15.894 24.106 15.894C23.766 15.894 23.428 15.958 23.178 16.084L20.008 17.718C19.532 17.946 18.904 17.61 18.598 16.984C18.292 16.358 18.452 15.662 18.928 15.2C19.08 15.05 19.258 14.936 19.466 14.828L22.636 13.194C23.564 12.734 25.076 12.734 26.004 13.194L29.364 14.922C33.348 16.842 33.348 16.842 33.348 21L33 20.416Z" fill="white" /></svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className='counter-box danger-box'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Active Users</h5>
                    <div className='CountUp-box'>
                      <h3>{(analyticsData['overview']?.['activeUsers'] || 0).toLocaleString()}</h3>
                    </div>
                    <small className={(analyticsData['overview']?.['userGrowth'] || 0) >= 0 ? 'text-success' : 'text-danger'}>
                      <i className={`icon-graph`}>
                        {(analyticsData['overview']?.['userGrowth'] || 0) >= 0
                          ? <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 0L12.2175 1.7175L8.5575 5.3775L5.5575 2.3775L0 7.9425L1.0575 9L5.5575 4.5L8.5575 7.5L13.2825 2.7825L15 4.5V0H10.5Z" fill="#34A853" /></svg>
                          : <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.5 9L12.2175 7.2825L8.5575 3.6225L5.5575 6.6225L0 1.0575L1.0575 0L5.5575 4.5L8.5575 1.5L13.2825 6.2175L15 4.5V9H10.5Z" fill="#EA4335" /></svg>
                        }
                      </i>
                      {Math.abs(analyticsData['overview']?.['userGrowth'] || 0)}%
                    </small>
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24.3741 11.416V8.82398C24.3741 4.67198 24.3741 4.67198 20.3901 2.75198L17.0301 1.02398C16.1501 0.567978 14.7181 0.567978 13.8381 1.02398L10.4781 2.75198C6.49407 4.67198 6.49407 4.67198 6.49407 8.82398V11.416C6.49407 15.568 6.49407 15.568 10.4781 17.488L13.8381 19.216C14.2781 19.444 14.8061 19.558 15.3341 19.558C15.8621 19.558 16.3901 19.444 16.8301 19.216L20.1901 17.488C24.1741 15.568 24.1741 15.568 24.1741 11.416Z" fill="white" /><path d="M33 20.416V24.568C33 28.72 33 28.72 29.016 30.64L25.656 32.368C25.216 32.596 24.688 32.71 24.16 32.71C23.632 32.71 23.104 32.596 22.664 32.368L19.504 30.734C19.32 30.636 19.16 30.518 19.008 30.394C18.424 29.932 18.424 29.236 18.73 28.61C19.036 27.984 19.642 27.648 20.118 27.876L23.29 29.51C23.788 29.752 24.532 29.752 25.03 29.51L28.39 27.782C29.552 27.172 29.552 26.59 29.552 25.178V20.416C29.552 19.004 29.552 18.422 28.39 17.812L25.03 16.084C24.782 15.958 24.444 15.894 24.106 15.894C23.766 15.894 23.428 15.958 23.178 16.084L20.008 17.718C19.532 17.946 18.904 17.61 18.598 16.984C18.292 16.358 18.452 15.662 18.928 15.2C19.08 15.05 19.258 14.936 19.466 14.828L22.636 13.194C23.564 12.734 25.076 12.734 26.004 13.194L29.364 14.922C33.348 16.842 33.348 16.842 33.348 21L33 20.416Z" fill="white" /></svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className='counter-box warning-box'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Agent Performance</h5>
                    <div className='CountUp-box'>
                      <h3>{(analyticsData['overview']?.['agentPerformance'] || 0)}%</h3>
                    </div>
                    <small className="text-muted">Avg success rate</small>
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24.3741 11.416V8.82398C24.3741 4.67198 24.3741 4.67198 20.3901 2.75198L17.0301 1.02398C16.1501 0.567978 14.7181 0.567978 13.8381 1.02398L10.4781 2.75198C6.49407 4.67198 6.49407 4.67198 6.49407 8.82398V11.416C6.49407 15.568 6.49407 15.568 10.4781 17.488L13.8381 19.216C14.2781 19.444 14.8061 19.558 15.3341 19.558C15.8621 19.558 16.3901 19.444 16.8301 19.216L20.1901 17.488C24.1741 15.568 24.1741 15.568 24.1741 11.416Z" fill="white" /><path d="M33 20.416V24.568C33 28.72 33 28.72 29.016 30.64L25.656 32.368C25.216 32.596 24.688 32.71 24.16 32.71C23.632 32.71 23.104 32.596 22.664 32.368L19.504 30.734C19.32 30.636 19.16 30.518 19.008 30.394C18.424 29.932 18.424 29.236 18.73 28.61C19.036 27.984 19.642 27.648 20.118 27.876L23.29 29.51C23.788 29.752 24.532 29.752 25.03 29.51L28.39 27.782C29.552 27.172 29.552 26.59 29.552 25.178V20.416C29.552 19.004 29.552 18.422 28.39 17.812L25.03 16.084C24.782 15.958 24.444 15.894 24.106 15.894C23.766 15.894 23.428 15.958 23.178 16.084L20.008 17.718C19.532 17.946 18.904 17.61 18.598 16.984C18.292 16.358 18.452 15.662 18.928 15.2C19.08 15.05 19.258 14.936 19.466 14.828L22.636 13.194C23.564 12.734 25.076 12.734 26.004 13.194L29.364 14.922C33.348 16.842 33.348 16.842 33.348 21L33 20.416Z" fill="white" /></svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className='counter-box success-box'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Conversion Rate</h5>
                    <div className='CountUp-box'>
                      <h3>{(analyticsData['overview']?.['conversionRate'] || 0)}%</h3>
                    </div>
                    <small className="text-muted">Leads to deals</small>
                  </div>
                  <div className='right-icon'>
                    <span className='icon'>
                      <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M24.3741 11.416V8.82398C24.3741 4.67198 24.3741 4.67198 20.3901 2.75198L17.0301 1.02398C16.1501 0.567978 14.7181 0.567978 13.8381 1.02398L10.4781 2.75198C6.49407 4.67198 6.49407 4.67198 6.49407 8.82398V11.416C6.49407 15.568 6.49407 15.568 10.4781 17.488L13.8381 19.216C14.2781 19.444 14.8061 19.558 15.3341 19.558C15.8621 19.558 16.3901 19.444 16.8301 19.216L20.1901 17.488C24.1741 15.568 24.1741 15.568 24.1741 11.416Z" fill="white" /><path d="M33 20.416V24.568C33 28.72 33 28.72 29.016 30.64L25.656 32.368C25.216 32.596 24.688 32.71 24.16 32.71C23.632 32.71 23.104 32.596 22.664 32.368L19.504 30.734C19.32 30.636 19.16 30.518 19.008 30.394C18.424 29.932 18.424 29.236 18.73 28.61C19.036 27.984 19.642 27.648 20.118 27.876L23.29 29.51C23.788 29.752 24.532 29.752 25.03 29.51L28.39 27.782C29.552 27.172 29.552 26.59 29.552 25.178V20.416C29.552 19.004 29.552 18.422 28.39 17.812L25.03 16.084C24.782 15.958 24.444 15.894 24.106 15.894C23.766 15.894 23.428 15.958 23.178 16.084L20.008 17.718C19.532 17.946 18.904 17.61 18.598 16.984C18.292 16.358 18.452 15.662 18.928 15.2C19.08 15.05 19.258 14.936 19.466 14.828L22.636 13.194C23.564 12.734 25.076 12.734 26.004 13.194L29.364 14.922C33.348 16.842 33.348 16.842 33.348 21L33 20.416Z" fill="white" /></svg>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Charts */}
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-8 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Revenue by Source</h4>
                      <div style={{ height: '300px' }}>
                        <Doughnut data={{ 
                          'labels': revenueDetails?.bySource?.labels || analyticsData['revenueBySource']?.['labels'] || ['Direct Sales', 'Inbound', 'Referrals', 'Partnerships'], 
                          'datasets': [{ 
                            'data': revenueDetails?.bySource?.values || analyticsData['revenueBySource']?.['data'] || [45, 25, 20, 10], 
                            'backgroundColor': ['#7F8BFA', 'var(--primary-color)', '#94C8FB', '#F7931A'] 
                          }] 
                        }} options={{ 'responsive': true, 'maintainAspectRatio': false }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-lg-4 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Monthly Recurring Revenue</h4>
                      <Table>
                        <tbody>
                          <tr>
                            <td>New MRR</td>
                            <td className="text-right text-success">+${(revenueDetails?.mrr?.new || analyticsData['mrr']?.['new'] || 0).toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td>Expansion MRR</td>
                            <td className="text-right text-success">+${(revenueDetails?.mrr?.expansion || analyticsData['mrr']?.['expansion'] || 0).toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td>Churned MRR</td>
                            <td className="text-right text-danger">-${(revenueDetails?.mrr?.churned || analyticsData['mrr']?.['churned'] || 0).toLocaleString()}</td>
                          </tr>
                          <tr>
                            <td>Contraction MRR</td>
                            <td className="text-right text-warning">-${(revenueDetails?.mrr?.contraction || analyticsData['mrr']?.['contraction'] || 0).toLocaleString()}</td>
                          </tr>
                          <tr className="font-weight-bold">
                            <td>Net MRR Growth</td>
                            <td className="text-right">${(revenueDetails?.mrr?.net || analyticsData['mrr']?.['net'] || 0).toLocaleString()}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </TabPane>

          {/* User Engagement Tab */}
          <TabPane tabId="engagement">
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-8 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>User Activity Comparison</h4>
                      <div style={{ height: '400px' }}>
                        <Radar data={userEngagementData} options={{ 'responsive': true, 'maintainAspectRatio': false }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-lg-4 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Engagement Metrics</h4>
                      <div className="progress-area-box mt-4">
                        <div className="d-flex justify-content-between">
                          <h5>Daily Active Users</h5>
                          <span>{analyticsData['engagement']?.['dau'] || '0'}%</span>
                        </div>
                        <Progress value={analyticsData['engagement']?.['dau'] || 0} color="primary" />
                      </div>
                      <div className="progress-area-box  mt-2">
                        <div className="d-flex justify-content-between">
                          <h5>Weekly Active Users</h5>
                          <span>{analyticsData['engagement']?.['wau'] || '0'}%</span>
                        </div>
                        <Progress value={analyticsData['engagement']?.['wau'] || 0} color="info" />
                      </div>
                      <div className="progress-area-box  mt-2">
                        <div className="d-flex justify-content-between">
                          <h5>Monthly Active Users</h5>
                          <span>{analyticsData['engagement']?.['mau'] || '0'}%</span>
                        </div>
                        <Progress value={analyticsData['engagement']?.['mau'] || 0} color="success" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </TabPane>

          {/* Performance Tab */}
          <TabPane tabId="performance">
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-6 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Conversion Funnel</h4>
                      <div style={{ height: '300px' }}>
                        <Bar data={conversionFunnelData} options={{ 'responsive': true, 'maintainAspectRatio': false, 'indexAxis': 'y' }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col-lg-6 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Agent Performance Distribution</h4>
                      <div style={{ height: '300px' }}>
                        <Doughnut data={agentPerformanceData} options={{ 'responsive': true, 'maintainAspectRatio': false }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </TabPane>

          {/* Custom Reports Tab */}
          <TabPane tabId="custom">
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-5 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Report Builder</h4>
                      <Form className="dashboard-form ">
                        <FormGroup>
                          <Label>Report Type</Label>
                          <Input type="select" value={reportType} onChange={(e) => setReportType(e['target']['value'])}>
                            <option value="summary">Executive Summary</option>
                            <option value="detailed">Detailed Analytics</option>
                            <option value="comparison">Period Comparison</option>
                            <option value="forecast">Revenue Forecast</option>
                          </Input>
                        </FormGroup>
                        <FormGroup>
                          <Label>Metrics to Include</Label>
                          {['revenue', 'users', 'agents', 'deals', 'calls', 'conversion'].map(metric => (
                            <div key={metric} className="custom-control custom-checkbox">
                              <input type="checkbox" className="custom-control-input" id={metric} checked={selectedMetrics.includes(metric)} onChange={(e) => {
                                if (e['target']['checked']) setSelectedMetrics([...selectedMetrics, metric]);
                                else setSelectedMetrics(selectedMetrics.filter(m => m !== metric));
                              }} />
                              <label className="custom-control-label" htmlFor={metric}>{metric.charAt(0).toUpperCase() + metric.slice(1)}</label>
                            </div>
                          ))}
                        </FormGroup>
                        <button 
                          className='btn-style-border w100 mt-3' 
                          onClick={generateCustomReport}
                          disabled={reportGenerating}
                        >
                          {reportGenerating ? (
                            <>
                              <i className="fa fa-spinner fa-spin mr-2"></i>Generating Report...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-download mr-2"></i>Generate & Download Report
                            </>
                          )}
                        </button>
                      </Form>
                    </div>
                  </div>
                </div>
                <div className='col-lg-7 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Recent Reports</h4>
                      <Table>
                        <thead>
                          <tr>
                            <th>Report Name</th>
                            <th>Type</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportsArray.map((report, idx) => (
                            <tr key={idx}>
                              <td>{report['name']}</td>
                              <td><Badge color="primary">{report['type']}</Badge></td>
                              <td>{new Date(report['createdAt']).toLocaleDateString()}</td>
                              <td>
                                <div className='action-box'>
                                  <button 
                                    className='action-btn' 
                                    color="primary"
                                    title="View Report Details"
                                  >
                                    <i className="icon">
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43" />
                                        <path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43" />
                                        <path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43" />
                                      </svg>
                                    </i>
                                  </button>
                                  <button 
                                    className='action-btn' 
                                    color="success-color"
                                    onClick={() => handleExport(report)}
                                    title="Download Report"
                                  >
                                    <i className="fa fa-download"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </TabPane>
        </TabContent>
      </div>
    </>

  );
};

export default AnalyticsReporting;